"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, Check, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
export default function Quote() {
  const introRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!introRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        const items = introRef.current!.querySelectorAll<HTMLElement>("[data-reveal]");
        gsap.from(items, {
          opacity: 0,
          y: 16,
          duration: 0.5,
          ease: "power2.out",
          stagger: 0.12,
          scrollTrigger: {
            trigger: introRef.current,
            start: "top 85%",
            once: true
          }
        });
      });
    }, introRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="32546" id="solicite-su-presupuesto" data-nav="light" className="bg-background py-28 md:py-40">
      <div className="mx-auto max-w-[1440px] px-6 md:px-14 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={introRef} className="lg:col-span-5">
            <div data-reveal className="flex items-center gap-3 mb-6" style={{
            color: "var(--primary)"
          }}>
              <span aria-hidden="true" className="block w-px h-3" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-sans font-semibold uppercase text-xs md:text-sm" style={{
              letterSpacing: "0.18em",
              lineHeight: 1
            }}>
                {"SOLICITE PRESUPUESTO"}
              </span>
              <span aria-hidden="true" className="block w-px h-3" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            <h2 data-reveal className="font-serif font-black uppercase text-foreground mb-10" style={{
            fontFamily: "var(--font-serif)",
            fontSize: "clamp(2.75rem, 6vw, 5.5rem)",
            lineHeight: 0.92,
            letterSpacing: "-0.005em"
          }}>
              {"Cuéntenos qué necesita."}
            </h2>

            <p data-reveal className="font-default text-base md:text-lg max-w-md mb-10" style={{
            color: "var(--muted-foreground)",
            lineHeight: 1.55
          }}>
              {"Rellene el formulario y le enviamos un presupuesto sin compromiso para su avería o proyecto en Totana, Murcia, Alicante o Almería."}
            </p>

            <Link to="tel:+34657068444" data-reveal className="flex items-center gap-3 pt-6 border-t transition-opacity hover:opacity-80" style={{
            borderColor: "var(--border)"
          }}>
              <Phone className="size-4" style={{
              color: "var(--foreground)"
            }} aria-hidden="true" />
              <span className="font-mono font-medium text-sm" style={{
              color: "var(--foreground)",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.02em"
            }}>
                {"¿PREFIERE LLAMAR? +34 657 068 444"}
              </span>
            </Link>
          </div>

          <div className="lg:col-span-7">
            <FormProvider formKey="solicitud_home" formId="solicitud_home" sectionName="solicite-su-presupuesto" defaultValues={{
            nombre: "",
            telefono: "",
            ciudad: "",
            mensaje: ""
          }} validationRules={{
            nombre: {
              required: true,
              minLength: 2
            },
            telefono: {
              required: true,
              pattern: "phone"
            },
            ciudad: {
              required: true
            }
          }} submitText="Enviar solicitud" successMessage="Hemos recibido su solicitud. Le respondemos en menos de 24 horas." errorMessage="No se ha podido enviar. Inténtelo de nuevo o llámenos al +34 657 068 444.">
              <QuoteForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-xl border p-10 md:p-12" style={{
      backgroundColor: "var(--background)",
      borderColor: "var(--border)"
    }}>
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg mb-6" style={{
        backgroundColor: "var(--primary)",
        color: "var(--primary-foreground)"
      }}>
          <Check className="size-7" aria-hidden="true" />
        </div>
        <h3 className="font-serif font-bold text-foreground" style={{
        fontFamily: "var(--font-serif)",
        fontSize: "clamp(1.75rem, 3vw, 2.25rem)",
        lineHeight: 1.1,
        letterSpacing: "-0.02em"
      }}>
          {successMessage}
        </h3>
      </div>;
  }
  const labelClass = "font-sans font-semibold uppercase text-xs mb-2 block";
  const labelStyle = {
    color: "var(--muted-foreground)",
    letterSpacing: "0.15em"
  } as const;
  const inputClass = "h-auto px-4 py-3.5 rounded-sm border bg-background text-foreground text-base";
  const inputStyle = {
    borderColor: "var(--border)",
    borderRadius: "8px"
  } as const;
  return <div className="rounded-xl border p-6 md:p-10" style={{
    backgroundColor: "var(--card)",
    borderColor: "var(--border)"
  }}>
      <Form onSubmit={handleSubmit} className="space-y-6">
        <FormField control={control} name="nombre" rules={{
        required: "Nombre requerido",
        minLength: {
          value: 2,
          message: "Mínimo 2 caracteres"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass} style={labelStyle}>
                {"Nombre"}
              </FormLabel>
              <FormControl>
                <Input placeholder="Su nombre" className={inputClass} style={inputStyle} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <FormField control={control} name="telefono" rules={{
          required: "Teléfono requerido"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClass} style={labelStyle}>
                  {"Teléfono"}
                </FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="657 068 444" className={inputClass} style={inputStyle} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="ciudad" rules={{
          required: "Ciudad requerida"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className={labelClass} style={labelStyle}>
                  {"Ciudad"}
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} value={field.value || ""}>
                    <SelectTrigger className="w-full h-auto px-4 py-3.5 rounded-sm border bg-background text-foreground text-base" style={inputStyle}>
                      <SelectValue placeholder="Seleccione su ciudad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Totana">Totana</SelectItem>
                      <SelectItem value="Murcia">Murcia</SelectItem>
                      <SelectItem value="Alicante">Alicante</SelectItem>
                      <SelectItem value="Almería">Almería</SelectItem>
                      <SelectItem value="Otra">Otra</SelectItem>
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="mensaje" render={({
        field
      }) => <FormItem>
              <FormLabel className={labelClass} style={labelStyle}>
                {"Mensaje"}
              </FormLabel>
              <FormControl>
                <Textarea placeholder="Cuéntenos brevemente qué necesita" rows={4} className="px-4 py-3.5 rounded-sm border bg-background text-foreground text-base min-h-[120px]" style={inputStyle} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 rounded-sm border" style={{
        backgroundColor: "color-mix(in srgb, var(--destructive) 8%, transparent)",
        borderColor: "var(--destructive)"
      }}>
            <p className="font-default text-sm" style={{
          color: "var(--destructive)"
        }}>
              {submitError}
            </p>
          </div>}

        <div>
          <Button type="submit" disabled={isSubmitting} className="w-full h-auto py-5 rounded-lg font-sans font-semibold uppercase text-sm group" style={{
          backgroundColor: "var(--primary)",
          color: "var(--primary-foreground)",
          letterSpacing: "0.08em"
        }}>
            <span>{isSubmitting ? "Enviando..." : "Enviar solicitud"}</span>
            <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </Button>
          <p className="text-center font-default text-xs mt-3" style={{
          color: "var(--muted-foreground)"
        }}>
            {"Le respondemos en menos de 24 horas."}
          </p>
        </div>
      </Form>
    </div>;
}